import type { CanvasCtx } from '@/features/studio/canvas/types'

export type CanvasScopeType =
  | 'production'
  | 'bible_character'
  | 'bible_location'
  | 'bible_prop'
  | 'bible_costume'
  | 'bible_style'
  | 'shot_scene'
  | 'shot'

export interface CanvasScope {
  scopeType: CanvasScopeType
  scopeKey: string
}

export interface CanvasScopeInput {
  ctx?: CanvasCtx | string
  scopeType?: string
  scopeKey?: string
  entityId?: string
  shotId?: string
  scriptId?: number
  episodeNo?: number
  sceneNo?: number
}

export function buildShotSceneScopeKey(scriptId: number, episodeNo: number, sceneNo: number): string {
  return `script:${scriptId}:ep:${episodeNo}:scene:${sceneNo}`
}

export function resolveCanvasScope(input: CanvasScopeInput): CanvasScope {
  if (input.scopeType && input.scopeKey) {
    return { scopeType: input.scopeType as CanvasScopeType, scopeKey: input.scopeKey }
  }

  const ctx = (input.ctx ?? 'free') as CanvasCtx
  switch (ctx) {
    case 'bible-character':
      if (!input.entityId) throw new Error('canvas scope: entityId required for bible-character')
      return { scopeType: 'bible_character', scopeKey: input.entityId }
    case 'bible-location':
      if (!input.entityId) throw new Error('canvas scope: entityId required for bible-location')
      return { scopeType: 'bible_location', scopeKey: input.entityId }
    case 'bible-prop':
      if (!input.entityId) throw new Error('canvas scope: entityId required for bible-prop')
      return { scopeType: 'bible_prop', scopeKey: input.entityId }
    case 'bible-costume':
      if (!input.entityId) throw new Error('canvas scope: entityId required for bible-costume')
      return { scopeType: 'bible_costume', scopeKey: input.entityId }
    case 'bible-style':
      return { scopeType: 'bible_style', scopeKey: 'default' }
    case 'shot-frame':
      if (input.scriptId != null && input.episodeNo != null && input.sceneNo != null) {
        return {
          scopeType: 'shot_scene',
          scopeKey: buildShotSceneScopeKey(input.scriptId, input.episodeNo, input.sceneNo),
        }
      }
      if (input.shotId) return { scopeType: 'shot', scopeKey: input.shotId }
      throw new Error('canvas scope: scriptId/episodeNo/sceneNo or shotId required for shot-frame')
    case 'shot-take':
      if (!input.shotId) throw new Error('canvas scope: shotId required for shot-take')
      return { scopeType: 'shot', scopeKey: input.shotId }
    case 'free':
    default:
      return { scopeType: 'production', scopeKey: 'default' }
  }
}

export function canvasScopeQuery(scope: CanvasScope): string {
  const q = new URLSearchParams()
  q.set('scope_type', scope.scopeType)
  q.set('scope_key', scope.scopeKey)
  return q.toString()
}
